import React, { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { mismatchAPI, reconciliationAPI } from '../services/api';

const WS_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

/**
 * 헷지 불일치 모니터링 페이지
 *
 * 검출 3종:
 *   - ENTRY_MISSING: 트레이더 진입했는데 마스터 진입 안 됨
 *   - CLOSE_MISSING: 트레이더 청산했는데 마스터 청산 안 됨
 *   - PARTIAL_CLOSE_MISMATCH: 부분 청산 lots 불일치
 *
 * 실시간 갱신 (10초 polling).
 * 알림은 Spring 스케줄러 (30초 주기 + 알림 지연) 가 자동 처리.
 */

type StatusFilter = 'unresolved' | 'resolved' | 'all';
type TypeFilter = '' | 'ENTRY_MISSING' | 'CLOSE_MISSING' | 'PARTIAL_CLOSE_MISMATCH';

interface MismatchRow {
  id: number;
  mismatch_type: string;
  point_hedge_position_id: number;
  account_id: string;
  master_account_id: string;
  customer_ticket: number;
  master_ticket: number | null;
  symbol: string;
  direction: string;
  lots: number;
  remaining_lots: number | null;
  detected_at: string;
  notified_at: string | null;
  resolved_at: string | null;
  resolution_type: string | null;
  resolution_note: string | null;
  detail_json: string | null;
  position_status?: string;
  opened_at?: string;
  closed_at?: string;
  master_close_price?: number;
}

interface RuleOption { id: number; name: string; master_account_id: string; account_count: number; }

const FlashValue: React.FC<{ value: number; color?: string }> = ({ value, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);
  return (
    <span style={{
      color: flash ? '#ffe066' : (color || 'inherit'),
      transition: 'color 0.4s ease-out',
      fontWeight: 600,
    }}>{value}</span>
  );
};

// 정렬 가능한 th
const SortTh: React.FC<{
  label: string; k: string;
  sortKey: string | null; sortDir: 'asc' | 'desc' | null;
  onSort: (k: string) => void; align?: 'left' | 'right';
}> = ({ label, k, sortKey, sortDir, onSort, align = 'left' }) => {
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : '') : ' ⇅';
  return (
    <th onClick={() => onSort(k)} style={{
      padding: '10px 12px', textAlign: align, fontWeight: 500,
      borderBottom: '1px solid #2a2f4a', whiteSpace: 'nowrap',
      cursor: 'pointer', userSelect: 'none', color: active ? '#6c8eef' : '#aaa',
    }}>{label}<span style={{ fontSize: 10 }}>{arrow}</span></th>
  );
};

const MismatchPage: React.FC<{ embedded?: boolean; hideKpi?: boolean; onSummaryChange?: (s: any) => void }> = ({ embedded = false, hideKpi = false, onSummaryChange }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('unresolved');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('');
  const [ruleId, setRuleId] = useState<string>('');
  const [accountId, setAccountId] = useState<string>('');

  const [rows, setRows] = useState<MismatchRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState<RuleOption[]>([]);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const [page, setPage] = useState(0);
  const pageSize = 50;

  const [autoRefresh, setAutoRefresh] = useState(false); // 자동 새로고침 (기본 OFF)
  // 정렬
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const toggleSort = (k: string) => {
    if (sortKey !== k) { setSortKey(k); setSortDir('asc'); setPage(0); }
    else if (sortDir === 'asc') { setSortDir('desc'); setPage(0); }
    else { setSortKey(null); setSortDir(null); setPage(0); }
  };
  // 서버 정렬 - rows 는 이미 정렬되어 옴

  // 룰 옵션 로드
  useEffect(() => {
    reconciliationAPI.getRules().then(r => setRules(r.data || [])).catch(() => {});
  }, []);

  // 필터 변경 시 page 0
  useEffect(() => {
    setPage(0);
  }, [statusFilter, typeFilter, ruleId, accountId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { status: statusFilter, page, size: pageSize };
      if (typeFilter) params.type = typeFilter;
      if (ruleId) params.ruleId = ruleId;
      if (accountId) params.accountId = accountId;
      if (sortKey) { params.sortBy = sortKey; params.sortDir = sortDir; }

      const r = await mismatchAPI.list(params);
      setRows(r.data.rows || []);
      setTotalCount(r.data.totalCount || 0);
      setSummary(r.data.summary || {});
      if (onSummaryChange) onSummaryChange(r.data.summary || {});
      setLastFetchedAt(new Date());
    } catch (e: any) {
      console.error('[MISMATCH] 로드 오류', e);
    } finally {
      setLoading(false);
    }
  };

  // 필터/정렬/페이지 변경 시 1회 페치
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, ruleId, accountId, page, sortKey, sortDir]);

  // 자동 새로고침 ON 일 때만 10초 polling + STOMP
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchData, 10_000);
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe('/topic/equity-updates', () => fetchData());
      },
    });
    client.activate();
    return () => {
      clearInterval(t);
      client.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, statusFilter, typeFilter, ruleId, accountId, page, sortKey, sortDir]);

  const handleResolve = async (id: number) => {
    const note = window.prompt('해결 메모 (선택사항):', '운영자 수동 확인');
    if (note === null) return; // 취소
    try {
      await mismatchAPI.resolve(id, note);
      await fetchData();
    } catch (e: any) {
      alert('해결 처리 실패: ' + (e?.response?.data?.error || e.message));
    }
  };

  const handleTrigger = async () => {
    if (!window.confirm('지금 즉시 미스매치 검사를 실행할까요?')) return;
    try {
      await mismatchAPI.triggerDetect();
      setTimeout(fetchData, 500);
    } catch (e: any) {
      alert('트리거 실패: ' + (e?.response?.data?.error || e.message));
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const fmtDate = (s?: string | null) => {
    if (!s) return '-';
    return s.substring(0, 19).replace('T', ' ');
  };

  const timeSince = (s?: string | null) => {
    if (!s) return '-';
    const d = new Date(s.replace(' ', 'T'));
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${diff}초`;
    if (diff < 3600) return `${Math.floor(diff / 60)}분 ${diff % 60}초`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 ${Math.floor((diff % 3600) / 60)}분`;
    return `${Math.floor(diff / 86400)}일`;
  };

  const typeBadge = (type: string) => {
    const map: any = {
      ENTRY_MISSING: { bg: '#3a1a1a', color: '#f87171', label: '🚨 마스터 진입 누락' },
      CLOSE_MISSING: { bg: '#3a2a1a', color: '#fbbf24', label: '⚠️ 마스터 청산 누락' },
      PARTIAL_CLOSE_MISMATCH: { bg: '#2a1a3a', color: '#a78bfa', label: '⚠️ 부분 청산 불일치' },
    };
    const c = map[type] || { bg: '#2a2a2a', color: '#aaa', label: type };
    return (
      <span style={{
        background: c.bg, color: c.color,
        padding: '4px 10px', borderRadius: 4,
        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
      }}>{c.label}</span>
    );
  };

  return (
    <div style={embedded
      ? { color: '#e0e0e0' }
      : { padding: 20, background: '#0f0f1a', color: '#e0e0e0', minHeight: '100vh' }}>
      {/* KPI 카드 - hideKpi 면 부모가 표시하므로 숨김 */}
      {!hideKpi && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ ...kpiCard, background: '#3a1a1a' }}>
          <div style={{ ...kpiLabel, color: '#f87171' }}>🚨 마스터 진입 누락</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#f87171' }}>
            <FlashValue value={Number(summary.entry_missing || 0)} color="#f87171" />
          </div>
        </div>
        <div style={{ ...kpiCard, background: '#3a2a1a' }}>
          <div style={{ ...kpiLabel, color: '#fbbf24' }}>⚠️ 마스터 청산 누락</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#fbbf24' }}>
            <FlashValue value={Number(summary.close_missing || 0)} color="#fbbf24" />
          </div>
        </div>
        <div style={{ ...kpiCard, background: '#2a1a3a' }}>
          <div style={{ ...kpiLabel, color: '#a78bfa' }}>⚠️ 부분 청산 불일치</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#a78bfa' }}>
            <FlashValue value={Number(summary.partial_mismatch || 0)} color="#a78bfa" />
          </div>
        </div>
        <div style={{ ...kpiCard, background: '#1a3a26' }}>
          <div style={{ ...kpiLabel, color: '#4ade80' }}>✅ 해결됨 (누적)</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#4ade80' }}>
            {summary.total_resolved || 0}
          </div>
        </div>
      </div>
      )}

      {/* 헤더 (자동검사 안내 + 버튼들) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#888' }}>
          30초마다 자동 검사 · 실시간 모니터링
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
          <span style={{ color: '#888' }}>마지막 갱신: {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '-'}</span>
          <button onClick={() => setAutoRefresh(v => !v)} style={{
            ...refreshBtnStyle,
            background: autoRefresh ? 'rgba(74,222,128,0.15)' : '#1a1f3a',
            color: autoRefresh ? '#4ade80' : '#888',
            border: `1px solid ${autoRefresh ? 'rgba(74,222,128,0.4)' : '#2a2f4a'}`,
            fontWeight: 600,
          }}>
            {autoRefresh ? '⏸ 자동 새로고침 ON' : '▶ 자동 새로고침 OFF'}
          </button>
          <button onClick={handleTrigger} style={triggerBtnStyle}>⚡ 즉시 검사</button>
          <button onClick={fetchData} disabled={loading} style={refreshBtnStyle}>
            {loading ? '⏳' : '🔄'} 새로고침
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 16, padding: 12,
        background: '#1a1f3a', borderRadius: 6, alignItems: 'center', flexWrap: 'wrap'
      }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as StatusFilter)} style={selectStyle}>
          <option value="unresolved">미해결만</option>
          <option value="resolved">해결됨</option>
          <option value="all">전체</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as TypeFilter)} style={selectStyle}>
          <option value="">모든 유형</option>
          <option value="ENTRY_MISSING">진입 누락</option>
          <option value="CLOSE_MISSING">청산 누락</option>
          <option value="PARTIAL_CLOSE_MISMATCH">부분 청산 불일치</option>
        </select>
        <select value={ruleId} onChange={e => setRuleId(e.target.value)} style={selectStyle}>
          <option value="">전체 룰</option>
          {rules.map(r => (
            <option key={r.id} value={r.id}>{r.name} (마스터 {r.master_account_id})</option>
          ))}
        </select>
        <input
          type="text" placeholder="계좌 ID" value={accountId}
          onChange={e => setAccountId(e.target.value)} style={inputStyle}
        />
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#888' }}>
          {totalCount.toLocaleString()}개
        </div>
      </div>

      {/* 테이블 */}
      {rows.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', background: '#1a1f3a', borderRadius: 6 }}>
          {statusFilter === 'unresolved' ? (
            <div style={{ color: '#4ade80', fontSize: 16 }}>
              ✅ 모든 헷지가 정상입니다
              <div style={{ fontSize: 13, color: '#888', marginTop: 8 }}>미해결 미스매치가 없습니다</div>
            </div>
          ) : (
            <div style={{ color: '#888' }}>데이터가 없습니다</div>
          )}
        </div>
      ) : (
        <div style={{ background: '#1a1f3a', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0f0f1a', color: '#aaa', fontSize: 12 }}>
                <SortTh label="유형" k="mismatch_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="계좌" k="account_id" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="티켓" k="customer_ticket" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="심볼" k="symbol" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Lots" k="lots" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="발견 시각" k="detected_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th style={thStyle}>경과</th>
                <SortTh label="상태" k="resolved_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th style={thStyle}>액션</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const resolved = r.resolved_at != null;
                return (
                  <tr key={r.id} style={{
                    borderTop: '1px solid rgba(42,47,74,0.5)',
                    opacity: resolved ? 0.55 : 1
                  }}>
                    <td style={tdStyle}>{typeBadge(r.mismatch_type)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>
                      {r.account_id}
                      <div style={{ fontSize: 10, color: '#888' }}>→ {r.master_account_id}</div>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                      <div>T: {r.customer_ticket}</div>
                      {r.master_ticket && <div style={{ color: '#888' }}>M: {r.master_ticket}</div>}
                    </td>
                    <td style={tdStyle}>
                      {r.symbol} <span style={{
                        color: r.direction === 'BUY' ? '#4ade80' : '#f87171', fontWeight: 600
                      }}>{r.direction}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                      {r.lots}
                      {r.remaining_lots != null && r.remaining_lots !== r.lots && (
                        <div style={{ fontSize: 10, color: '#888' }}>남은 {r.remaining_lots}</div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: '#aaa' }}>{fmtDate(r.detected_at)}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: resolved ? '#888' : '#fbbf24', fontWeight: 600 }}>
                      {timeSince(resolved ? r.resolved_at : r.detected_at)}
                    </td>
                    <td style={tdStyle}>
                      {resolved ? (
                        <div>
                          <div style={{ color: '#4ade80', fontSize: 11 }}>✅ 해결</div>
                          {r.resolution_type && (
                            <div style={{ fontSize: 10, color: '#666' }}>{r.resolution_type}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#f87171', fontSize: 11 }}>🔴 미해결</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {!resolved && (
                        <button onClick={() => handleResolve(r.id)} style={resolveBtnStyle}>
                          해결됨 표시
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이징 */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button onClick={() => setPage(0)} disabled={page === 0} style={pageBtnStyle}>«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pageBtnStyle}>‹</button>
          <span style={{ fontSize: 13, color: '#aaa' }}>{page + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pageBtnStyle}>›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={pageBtnStyle}>»</button>
        </div>
      )}
    </div>
  );
};

const kpiCard: React.CSSProperties = { padding: '14px 18px', borderRadius: 6 };
const kpiLabel: React.CSSProperties = { fontSize: 12, marginBottom: 8 };
const selectStyle: React.CSSProperties = {
  background: '#0f0f1a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', fontSize: 13
};
const inputStyle: React.CSSProperties = {
  background: '#0f0f1a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', fontSize: 13, width: 120
};
const thStyle: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 500,
  borderBottom: '1px solid #2a2f4a', whiteSpace: 'nowrap'
};
const tdStyle: React.CSSProperties = { padding: '10px 12px' };
const refreshBtnStyle: React.CSSProperties = {
  background: '#1a1f3a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', padding: '6px 14px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13
};
const triggerBtnStyle: React.CSSProperties = {
  background: 'rgba(102,126,234,0.2)', color: '#a78bfa',
  border: '1px solid rgba(102,126,234,0.4)', padding: '6px 14px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600
};
const resolveBtnStyle: React.CSSProperties = {
  background: 'rgba(74,222,128,0.15)', color: '#4ade80',
  border: '1px solid rgba(74,222,128,0.3)', padding: '4px 10px',
  borderRadius: 4, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap'
};
const pageBtnStyle: React.CSSProperties = {
  background: '#1a1f3a', color: '#e0e0e0',
  border: '1px solid #2a2f4a', borderRadius: 4,
  padding: '6px 12px', cursor: 'pointer', fontSize: 13
};

export default MismatchPage;
